import {tva} from "@gluestack-ui/nativewind-utils/tva";
import type React from "react";
import type {ComponentProps} from "react";
import {memo, useCallback} from "react";
import type {ImageStyle, StyleProp} from "react-native";
import ImageView from "react-native-image-viewing";

import {useDisclose} from "../../react";
import ExpoImageWithClassNames from "../ExpoImageWithClassNames";
import ImagePreview from "../ImagePreview";
import ImageRenderer from "../ImageRenderer";
import {Box} from "../ui/box";
import {Pressable} from "../ui/pressable";

const defaultRect = {width: 400, height: 0};

type Props = {
    uri: string;
    imageAlt: string;
    size?: "sm" | "md" | "lg";
    className?: string;
    onLoad?: () => void;
};

const viewImageContainerStyles = tva({
    base: "flex-shrink-0 flex-grow-0 overflow-hidden w-full",
    variants: {
        size: {
            sm: "rounded-md md:w-[48px] aspect-square",
            md: "rounded-lg w-full md:w-[165px] aspect-[3/2]",
            lg: "rounded-sm w-full md:w-[270px] aspect-[3/2]"
        }
    }
});

const ImageViewer: React.FC<Props> = ({uri, imageAlt, size, className, onLoad}) => {
    const {isOpen, onOpen, onClose} = useDisclose();

    const imageRenderer = useCallback<
        NonNullable<ComponentProps<typeof ImageRenderer>["imageRenderer"]>
    >(
        ({url, containerRect, imageSize, imageAlt}) => {
            let style: React.CSSProperties | undefined;
            if (!size) {
                if (imageSize.width >= containerRect.width) {
                    const ratio = imageSize.width / imageSize.height;
                    const nextHeight = Math.round(containerRect.width / ratio);

                    style = {
                        maxWidth: containerRect.width,
                        aspectRatio: `${containerRect.width} / ${nextHeight}`,
                        width: "100%" as const
                    };
                } else {
                    style = {
                        maxWidth: imageSize.width,
                        aspectRatio: `${imageSize.width} / ${imageSize.height}`,
                        width: "100%" as const
                    };
                }
            } else if (process.env.IS_WEB) {
                style = {
                    width: "100%",
                    height: "100%"
                };
            }

            if (process.env.IS_WEB) {
                return (
                    <>
                        <ImagePreview
                            imageAlt={imageAlt}
                            isOpen={isOpen}
                            url={url}
                            onClose={onClose}
                        />
                        <div
                            aria-label="View image"
                            className={className}
                            data-testid="image-viewer-image"
                            role="button"
                            style={{
                                cursor: "zoom-in",
                                display: "flex",
                                justifyContent: "center",
                                ...style
                            }}
                            tabIndex={0}
                            onClick={onOpen}
                            onKeyDown={onOpen}
                        >
                            <div
                                style={{
                                    background: `url("${url}") no-repeat 50% 50% / cover`,
                                    ...style
                                }}
                            />
                        </div>
                    </>
                );
            }

            return (
                <ExpoImageWithClassNames
                    cachePolicy="disk"
                    className={viewImageContainerStyles({size, className})}
                    contentFit="cover"
                    source={{uri: url}}
                    style={style as StyleProp<ImageStyle>}
                />
            );
        },
        [size, className, isOpen, onClose, onOpen]
    );

    if (process.env.IS_WEB) {
        if (!size) {
            return (
                <ImageRenderer
                    containerRect={defaultRect}
                    imageAlt={imageAlt}
                    imageRenderer={imageRenderer}
                    url={uri}
                    onLoad={onLoad}
                />
            );
        }

        return (
            <Box className={viewImageContainerStyles({size})} testID="image-viewer">
                <ImageRenderer
                    containerRect={defaultRect}
                    imageAlt={imageAlt}
                    imageRenderer={imageRenderer}
                    url={uri}
                    onLoad={onLoad}
                />
            </Box>
        );
    }

    if (isOpen) {
        return (
            // this lib will allow us to preview multiple images
            // so that we can change to support multiple images later
            <ImageView imageIndex={0} images={[{uri}]} visible={isOpen} onRequestClose={onClose} />
        );
    }

    return (
        <Pressable onPress={onOpen}>
            <ImageRenderer
                containerRect={defaultRect}
                imageAlt={imageAlt}
                imageRenderer={imageRenderer}
                url={uri}
                onLoad={onLoad}
            />
        </Pressable>
    );
};

export default memo(ImageViewer);
